import { WebSocketMessage, MessageType } from "../types";
import { WS_BASE_URL } from "../config";

class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: Map<MessageType, (data: any) => void> = new Map();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private onConnectionEstablishedCallback: (() => void) | null = null;

  constructor(private url: string = `${WS_BASE_URL}/ws`) {}

  onConnectionEstablished(callback: () => void): void {
    this.onConnectionEstablishedCallback = callback;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        return;
      }

      this.isConnecting = true;

      try {
        this.socket = new WebSocket(this.url);

        this.socket.onopen = () => {
          console.log("WebSocket connection established");
          this.isConnecting = false;
          this.reconnectAttempts = 0;

          if (this.onConnectionEstablishedCallback) {
            this.onConnectionEstablishedCallback();
          }

          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            console.log("Message received:", message);
            this.handleMessage(message);
          } catch (error) {
            console.error("Failed to parse message:", error);
          }
        };

        this.socket.onclose = (event) => {
          console.log("WebSocket connection closed:", event.code, event.reason);
          this.isConnecting = false;

          if (
            !event.wasClean &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            setTimeout(() => {
              this.reconnectAttempts++;
              console.log(
                `Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
              );
              this.connect().catch(console.error);
            }, this.reconnectInterval);
          }
        };

        this.socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          this.isConnecting = false;
          reject(new Error("WebSocket connection failed"));
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close(1000, "Client disconnect");
      this.socket = null;
    }
  }

  sendMessage(type: MessageType, data: any = {}): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = { type, data };
      this.socket.send(JSON.stringify(message));
      console.log("Sending message:", message);
    } else {
      console.error("WebSocket not connected, cannot send message");
    }
  }

  onMessage(type: MessageType, handler: (data: any) => void): void {
    this.messageHandlers.set(type, handler);
  }

  offMessage(type: MessageType): void {
    this.messageHandlers.delete(type);
  }

  private handleMessage(message: WebSocketMessage): void {
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message.data);
    }
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getReadyState(): number | undefined {
    return this.socket?.readyState;
  }
}

export default WebSocketService;
